import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../styles/DiagramacionMensual.css";
import React, { useMemo, useState, useCallback, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/** Códigos válidos */
const TURNOS = ["", "FN", "F", "FC", "GD", "N"];

const MONTHS = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

const BASES = ["Taller Central", "Punta de Línea", "Base Norte", "Base Sur"];

const SUBAREAS_TECNICA = [
  "Novedades Turno Noche",
  "Limpieza",
  "Carga de Fluidos",
  "ITV",
  "Mecánica",
  "Electricidad",
];

function daysInMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

function makeId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const codeToCellStyle = (code) => {
  switch (code) {
    case "FN":
      return { backgroundColor: "#4ade80", color: "#0f172a", fontWeight: 700 };
    case "F":
      return { backgroundColor: "#cbd5e1", color: "#0f172a", fontWeight: 700 };
    case "FC":
      return { backgroundColor: "#000000", color: "#ffffff", fontWeight: 700 };
    case "GD":
      return { backgroundColor: "#d946ef", color: "#ffffff", fontWeight: 700 };
    case "N":
      return { backgroundColor: "#ffffff", color: "#0f172a" };
    default:
      return { backgroundColor: "#ffffff", color: "#334155" };
  }
};

export default function DiagramacionMensual() {
  const gridRef = useRef(null);

  const [year, setYear] = useState(2025);
  const [month, setMonth] = useState(11);
  const [base, setBase] = useState("Taller Central");
  const [subArea, setSubArea] = useState("Novedades Turno Noche");
  const [excelLoaded, setExcelLoaded] = useState(false);

  const [rowData, setRowData] = useState([]);

  // Para SWAP de celdas (click 1 -> click 2)
  const [swapCell, setSwapCell] = useState(null); // { rowId, field }

  const totalDays = useMemo(() => daysInMonth(year, month), [year, month]);

  /** Import Excel */
  const handleImportExcel = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const empleados = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const total = daysInMonth(year, month);

      const rows = empleados.map((r) => {
        const row = {
          id: makeId(),
          personal: r.NombreApellido || r["Nombre Apellido"] || r.Personal || r.personal || "",
          leg: r.Legajo || r.leg || "",
          sector: r.Sector || r.sector || "",
          turno: r.Turno || r.turno || "NOCHE",
          base: r.Base || r.base || "",
          funcion: r.Funcion || r.funcion || "OPERARIO",
        };
        for (let d = 1; d <= total; d++) row[`d${d}`] = r[`d${d}`] ?? ""; // si el excel ya trae d1..dN
        return row;
      });

      setRowData(rows);
      setExcelLoaded(true);
      e.target.value = null;
    };

    reader.readAsArrayBuffer(file);
  };

  /** Export Excel */
  const handleExportExcel = () => {
    if (!rowData.length) return window.alert("No hay datos para exportar");

    const total = daysInMonth(year, month);
    const dayCols = Array.from({ length: total }, (_, i) => `d${i + 1}`);

    // Cabeceras amigables
    const data = rowData.map((row) => {
      const out = {
        PERSONAL: row.personal ?? "",
        LEGAJO: row.leg ?? "",
        SECTOR: row.sector ?? "",
        TURNO: row.turno ?? "",
        BASE: row.base ?? "",
        FUNCION: row.funcion ?? "",
      };
      dayCols.forEach((c, i) => {
        out[String(i + 1)] = row[c] ?? "";
      });
      return out;
    });

    const ws = XLSX.utils.json_to_sheet(data);

    // Ajuste ancho columnas (simple)
    const cols = [
      { wch: 28 }, { wch: 10 }, { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 16 },
      ...Array.from({ length: total }, () => ({ wch: 4 })),
    ];
    ws["!cols"] = cols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Diagramacion");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `diagramacion_${year}_${month + 1}.xlsx`
    );
  };

  /** Simular empleados */
  const handleSimularEmpleados = () => {
    const total = daysInMonth(year, month);
    const nombres = [
      "JUAN PEREZ","ANA GOMEZ","CARLOS LOPEZ","MARIA DIAZ","PEDRO SANCHEZ",
      "LAURA FERNANDEZ","MARTIN RAMIREZ","SOFIA ALVAREZ","DIEGO TORRES","VALERIA MORALES",
      "RODRIGO SUAREZ","CECILIA VERA","GUSTAVO MORA","ANDREA LUNA","LUIS CASTRO",
    ];

    const rows = nombres.map((nombre, i) => {
      const row = {
        id: makeId(),
        personal: nombre,
        leg: 1000 + i,
        sector: "",
        turno: "NOCHE",
        base: base,
        funcion: "OPERARIO",
      };
      for (let d = 1; d <= total; d++) row[`d${d}`] = "";
      return row;
    });

    setRowData(rows);
  };

  /** Diagramación simulada */
  const handleDiagramar = () => {
    const total = daysInMonth(year, month);
    const domingos = [];
    for (let d = 1; d <= total; d++) {
      const fecha = new Date(year, month, d);
      if (fecha.getDay() === 0) domingos.push(d);
    }

    setRowData((prev) =>
      prev.map((row, idx) => {
        const nuevo = { ...row };
        for (let d = 1; d <= total; d++) nuevo[`d${d}`] = "N";
        domingos.forEach((d) => (nuevo[`d${d}`] = "F"));

        const guardias = domingos.slice(0);
        for (let g = 0; g < 2 && guardias.length > 0; g++) {
          const diaGuardia = guardias[(idx + g) % guardias.length];
          if (diaGuardia) nuevo[`d${diaGuardia}`] = "GD";
          if (diaGuardia && diaGuardia + 2 <= total) nuevo[`d${diaGuardia + 2}`] = "FC";
        }
        return nuevo;
      })
    );
  };

  /** Agregar fila */
  const handleAddRow = () => {
    setRowData((prev) => {
      const newRow = {
        id: makeId(),
        personal: "NUEVO PERSONAL",
        leg: "",
        sector: "",
        turno: "NOCHE",
        base: base,
        funcion: "OPERARIO",
      };
      for (let d = 1; d <= totalDays; d++) newRow[`d${d}`] = "";
      return [...prev, newRow];
    });

    setTimeout(() => {
      const api = gridRef.current?.api;
      if (!api) return;
      api.ensureIndexVisible(rowData.length, "bottom");
    }, 0);
  };

  /** Vaciar mes */
  const handleClearMonth = () => {
    if (!window.confirm("¿Vaciar todas las celdas del mes para todos?")) return;
    setRowData((prev) =>
      prev.map((r) => {
        const next = { ...r };
        for (let d = 1; d <= totalDays; d++) next[`d${d}`] = "";
        return next;
      })
    );
  };

  /** Validación de valores */
  const normalizeTurno = (value) => (TURNOS.includes(value) ? value : "");

  /** Cambio de celda (cuando editás) */
  const onCellValueChanged = useCallback((params) => {
    const field = params.colDef.field;
    if (!field || !field.startsWith("d")) return;

    const value = normalizeTurno(params.newValue);

    setRowData((prev) => {
      const next = [...prev];
      const idx = params.node.rowIndex;
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  }, []);

  /** SWAP por click: click celda A -> click celda B */
  const onCellClicked = useCallback((params) => {
    const field = params.colDef.field;
    if (!field || !field.startsWith("d")) return;

    const rowId = params.data?.id;
    if (!rowId) return;

    // si no hay celda seleccionada, guardo la primera
    if (!swapCell) {
      setSwapCell({ rowId, field });
      return;
    }

    // si clickeás la misma celda, cancelá
    if (swapCell.rowId === rowId && swapCell.field === field) {
      setSwapCell(null);
      return;
    }

    // intercambio
    setRowData((prev) => {
      const next = prev.map((r) => ({ ...r }));
      const a = next.find((r) => r.id === swapCell.rowId);
      const b = next.find((r) => r.id === rowId);
      if (!a || !b) return prev;

      const tmp = a[swapCell.field] ?? "";
      a[swapCell.field] = b[field] ?? "";
      b[field] = tmp;

      return next;
    });

    setSwapCell(null);
  }, [swapCell]);

  /** Columnas */
  const columnDefs = useMemo(() => {
    const fixed = [
      { headerName: "PERSONAL", field: "personal", pinned: "left", width: 220, editable: false, cellStyle: { fontWeight: 700 } },
      { headerName: "LEG", field: "leg", pinned: "left", width: 90, editable: false },
      { headerName: "SECTOR", field: "sector", pinned: "left", width: 120, editable: false },
      { headerName: "TURNO", field: "turno", pinned: "left", width: 110, editable: false },
      { headerName: "BASE", field: "base", pinned: "left", width: 140, editable: false },
      { headerName: "FUNCIÓN", field: "funcion", pinned: "left", width: 160, editable: false },
    ];

    const days = Array.from({ length: totalDays }, (_, i) => {
      const day = i + 1;
      const field = `d${day}`;

      return {
        headerName: String(day),
        field,
        width: 64,
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: TURNOS },
        valueFormatter: (p) => p.value ?? "",
        cellStyle: (p) => {
          const style = codeToCellStyle(p.value);
          const isSwapFocus = swapCell && swapCell.rowId === p.data?.id && swapCell.field === field;
          return {
            ...style,
            textAlign: "center",
            borderRadius: "8px",
            cursor: "pointer",
            outline: isSwapFocus ? "2px solid #0ea5e9" : undefined,
            outlineOffset: isSwapFocus ? "1px" : undefined,
          };
        },
      };
    });

    return [...fixed, ...days];
  }, [totalDays, swapCell]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: false,
      filter: false,
    }),
    []
  );

  return (
    <div className="dm-page">
      <div className="dm-container">
        <div className="dm-card">
          <div className="dm-header">
            <div>
              <h1 className="dm-title">Diagramación mensual</h1>
              <p className="dm-subtitle">Base/Sub-área por mes (tipo Excel)</p>
              {swapCell && (
                <p style={{ marginTop: 8, fontSize: 12, color: "#0f172a" }}>
                  Modo intercambio: seleccionaste una celda. Click en otra para intercambiar (click en la misma para cancelar).
                </p>
              )}
            </div>

            {!excelLoaded && (
              <div className="dm-filters">
                <label>
                  Año
                  <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
                </label>

                <label>
                  Mes
                  <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                    {MONTHS.map((m, idx) => (
                      <option key={m} value={idx}>{m}</option>
                    ))}
                  </select>
                </label>

                <label>
                  Base
                  <select value={base} onChange={(e) => setBase(e.target.value)}>
                    {BASES.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </label>

                <label>
                  Sub-área
                  <select value={subArea} onChange={(e) => setSubArea(e.target.value)}>
                    {SUBAREAS_TECNICA.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>

          <div className="dm-toolbar">
            <div className="dm-legend">
              <span className="tag tag-fn">FN</span>
              <span className="tag tag-f">F</span>
              <span className="tag tag-fc">FC</span>
              <span className="tag tag-gd">GD</span>
              <span className="tag tag-n">N / —</span>
            </div>

            <div className="dm-actions">
              <button className="btnDiagramacionMensual btn-ghost" onClick={handleAddRow}>
                ➕ Agregar personal
              </button>

              <button className="btnDiagramacionMensual btn-ghost" onClick={handleClearMonth}>
                🧹 Vaciar mes
              </button>

              <label className="btnDiagramacionMensual btn-ghost" style={{ marginBottom: 0 }}>
                📤 Importar Excel
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                  onChange={handleImportExcel}
                />
              </label>

              <button className="btnDiagramacionMensual btn-ghost" onClick={handleSimularEmpleados}>
                🧪 Simular empleados demo
              </button>

              <button className="btnDiagramacionMensual btn-ghost" onClick={handleDiagramar} disabled={rowData.length === 0}>
                ⚡ Diagramar automáticamente
              </button>

              <button className="btnDiagramacionMensual btn-primary" onClick={handleExportExcel}>
                📥 Exportar a Excel
              </button>
            </div>
          </div>
        </div>

        <div className="dm-card dm-card--grid">
          <div className="dm-gridWrap">
            <div className="ag-theme-quartz dm-grid" style={{ height: 500, width: "100%" }}>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={(p) => p.data.id}
                onCellValueChanged={onCellValueChanged}
                onCellClicked={onCellClicked}
                stopEditingWhenCellsLoseFocus
                animateRows
              />
            </div>
          </div>

          <p className="dm-help">
            Tip: click en una celda → elegí el código. Copiar/pegar también funciona. Para intercambiar: click celda A y luego celda B.
          </p>
        </div>
      </div>
    </div>
  );
}
